/**
 * SidebarFacetList — reusable facet block (label + checkbox/radio rows with counts).
 * Controlled by requirementsViewStore.
 */
import type { ReactNode } from 'react'

export interface FacetItem {
  value: string
  label: string
  count?: number
  /** Optional glyph to show left of the label (colored dot, hash, etc.) */
  glyph?: ReactNode
}

interface Props {
  title: string
  items: FacetItem[]
  selected: Set<string>
  onToggle: (value: string) => void
  /** Render radios (single-select) instead of checkboxes (multi). */
  mode?: 'checkbox' | 'radio'
  footer?: ReactNode
}

export function SidebarFacetList({ title, items, selected, onToggle, mode = 'checkbox', footer }: Props) {
  return (
    <div className="mb-3">
      <div
        className="flex items-center justify-between px-1 mb-1"
        style={{
          fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--color-fg-muted)',
        }}
      >
        <span>{title}</span>
      </div>
      <ul className="list-none p-0 m-0">
        {items.map((item) => {
          const isActive = selected.has(item.value)
          return (
            <li key={item.value}>
              <button
                type="button"
                onClick={() => onToggle(item.value)}
                className="w-full flex items-center gap-2.5 px-1.5 py-[3px] rounded transition-colors text-left"
                style={{
                  fontSize: 12.5,
                  color: isActive ? 'var(--color-fg)' : 'var(--color-fg-secondary)',
                  fontWeight: isActive ? 500 : 400,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Indicator active={isActive} mode={mode} />
                {item.glyph && <span className="flex-shrink-0">{item.glyph}</span>}
                <span className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                  {item.label}
                </span>
                {typeof item.count === 'number' && (
                  <span className="text-[11px] tabular-nums" style={{ color: 'var(--color-fg-muted)' }}>
                    {item.count}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ul>
      {footer && <div className="mt-1">{footer}</div>}
    </div>
  )
}

function Indicator({ active, mode }: { active: boolean; mode: 'checkbox' | 'radio' }) {
  if (mode === 'radio') {
    return (
      <span
        aria-hidden
        className="flex-shrink-0 flex items-center justify-center"
        style={{
          width: 14, height: 14,
          borderRadius: '50%',
          border: active ? '1px solid var(--color-fg)' : '1px solid var(--color-border-strong)',
        }}
      >
        {active && (
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fg)' }} />
        )}
      </span>
    )
  }
  return (
    <span
      aria-hidden
      className="flex-shrink-0 relative"
      style={{
        width: 14, height: 14, borderRadius: 3,
        background: active ? 'var(--color-fg)' : 'transparent',
        border: `1px solid ${active ? 'var(--color-fg)' : 'var(--color-border-strong)'}`,
      }}
    >
      {active && (
        <span
          aria-hidden
          style={{
            position: 'absolute', left: 3, top: 0,
            width: 5, height: 8,
            borderRight: '1.6px solid var(--color-bg-card)',
            borderBottom: '1.6px solid var(--color-bg-card)',
            transform: 'rotate(45deg)',
          }}
        />
      )}
    </span>
  )
}
